import chroma from "chroma-js";

import { DEVICE } from "base/constants";
import {
  generateShade,
  generateTint,
  isContrastColor,
  getRGB
} from "base/utils";

import { DEFAULT_FONT } from "../constants/index.js";

function generateLightModeColors(hex, kind) {
  const [rgbArr, alpha] = getRGB(hex);
  let colors = { ...generateGrayPallet(rgbArr) };

  if (kind === DEVICE.PWA) {
    colors = {
      ...colors,
      500: generateTint(rgbArr, 0),
      400: generateTint(rgbArr, 25),
      300: generateTint(rgbArr, 50), //lightest
      200: generateTint(rgbArr, 70),
      100: generateTint(rgbArr, 90), //light
      dark: generateShade(rgbArr, 40)
    };
  } else {
    colors = {
      ...colors,
      100: generateTint(rgbArr, 95),
      200: generateTint(rgbArr, 85),
      300: generateTint(rgbArr, 65),
      400: generateTint(rgbArr, 40),
      500: generateTint(rgbArr, 0),
      600: generateShade(rgbArr, 40),
      700: generateShade(rgbArr, 66),
      800: generateShade(rgbArr, 93)
    };
  }

  for (let color in colors) {
    colors[color] = chroma([...colors[color], alpha]).hex();
  }

  return colors;
}

function generateDarkModeColors(hex, kind) {
  const [rgbArr, alpha] = getRGB(hex);
  let darkModeRgb = generateTint(rgbArr, 40);

  let colors = {};

  if (kind === DEVICE.PWA) {
    // same color variables for pwa dark and light mode
    colors = {
      500: generateTint(rgbArr, 0),
      400: generateTint(rgbArr, 25),
      300: generateTint(rgbArr, 50), //lightest
      200: generateTint(rgbArr, 70),
      100: generateTint(rgbArr, 90), //light
      dark: generateShade(rgbArr, 40)
    };
  } else {
    colors = {
      100: generateShade(darkModeRgb, 85),
      200: generateShade(darkModeRgb, 79),
      300: generateShade(darkModeRgb, 63),
      400: generateShade(darkModeRgb, 30),
      500: generateShade(darkModeRgb, 0),
      600: generateTint(rgbArr, 67),
      700: generateTint(rgbArr, 80),
      800: generateTint(rgbArr, 93)
    };
  }
  for (let color in colors) {
    colors[color] = chroma([...colors[color], alpha]).hex();
  }

  return colors;
}

function generateGrayPallet(rgbArr, type) {
  let tintGrayList = generateTint(rgbArr, 80);

  return {
    page: generateTint(rgbArr, 95),
    gray100: generateTint(rgbArr, 90),
    gray200: generateTint(rgbArr, 80),
    gray300: generateTint(rgbArr, 70),
    gray400: generateTint(rgbArr, 60),
    gray500: generateTint(rgbArr, 50),
    gray600: generateShade(tintGrayList, 25),
    gray700: generateShade(tintGrayList, 45),
    gray800: generateShade(tintGrayList, 65),
    gray900: generateShade(tintGrayList, 85)
  };
}

export function generateColors(hex = "#000", kind = DEVICE.WEB) {
  let [rgbArr] = getRGB(hex);

  let colorPallet = {
    lightPallet: generateLightModeColors(hex, kind),
    darkPallet: generateDarkModeColors(hex, kind)
  };

  return {
    colorPallet,
    isContrast: isContrastColor(rgbArr)
  };
}

export function isHex(hex) {
  let regEx = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

  let hexWoHash = [...(hex.includes("#") ? hex.slice(1) : hex)];
  if (hexWoHash.length === 3) {
    hex = hexWoHash.map((i) => i + i).join("");
  }
  if (regEx.exec(hex) !== null) {
    return true;
  }
  return false;
}

const genrateVariablesStr = (colorObj = {}) => {
  let colors = "";
  let grays = "";
  for (let color in colorObj) {
    if (color.includes("page")) {
      grays = `${grays}--kf-color-bg-white:${colorObj[color]}; 
    --kf-color-bg-gray:${colorObj[color]};
    --kf-color-bg-overlay-white:${colorObj[color]};
    `;
    } else if (color.includes("gray")) {
      grays = `${grays}--${"kf-color-gray"}-${color.slice(-3)}:${
        colorObj[color]
      };`;
    } else {
      colors = `${colors}--kf-color-primary-${color}:${colorObj[color]};`;
    }
  }
  return { colors, grays };
};

const createPWAvariablesStr = (colorObj = {}) => {
  let { lightPallet, darkPallet } = colorObj;

  let lightVars = genrateVariablesStr(lightPallet);
  let darkVars = genrateVariablesStr(darkPallet);

  let light = `--kf-color-primary-alpha-50:${lightPallet["500"]}80;\n
  --kf-color-primary-alpha-15:${lightPallet["500"]}26;\n
  ${lightVars.colors}`;

  let grayLight = `--kf-color-gray-200:${lightPallet.gray200};\n
  --kf-color-gray-300-outline-bright-in-dark:${lightPallet.gray300};\n
  ${lightVars.grays}`;

  let dark = `--kf-color-primary-alpha-50:${darkPallet["500"]}80;\n
  --kf-color-primary-alpha-15:${darkPallet["500"]}26;\n
  ${darkVars.colors}`;

  return { light, grayLight, dark };
};

const createWEBvariablesStr = (colorObj = {}, isContrast = false) => {
  let { lightPallet, darkPallet } = colorObj;

  let lightVars = genrateVariablesStr(lightPallet);
  let darkVars = genrateVariablesStr(darkPallet);

  let light = `--kf-color-white:${isContrast ? "#282d36" : "#ffffff"};\n
  ${lightVars.colors}`;

  let grayLight = `--kf-color-gray-alpha-25:${lightPallet.gray400}40;\n
  --kf-color-gray-alpha-50:${lightPallet.gray400}80;\n
  --kf-color-gray-alpha-75:${lightPallet.gray400}bf;\n
  ${lightVars.grays}`;

  let dark = `--kf-color-white:${isContrast ? "#ffffff" : "#282d36"};\n
    ${darkVars.colors}`;

  return { light, grayLight, dark };
};

export const getColorVariables = (
  color,
  changeBg = false,
  fontName = DEFAULT_FONT,
  kind = DEVICE.WEB
) => {
  let { colorPallet, isContrast } = generateColors(color, kind);

  var { light, grayLight, dark } =
    kind === DEVICE.PWA
      ? createPWAvariablesStr(colorPallet)
      : createWEBvariablesStr(colorPallet, isContrast, fontName);

  let cssTextContent = `body{${light}${`--kf-font-family: ${fontName};`}${
    changeBg ? grayLight : ""
  }}\nbody.dark,body.dim{${dark}}`;

  return [cssTextContent, isContrast];
};
